/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import moment from 'moment-timezone';

import { ApiStatus } from '../../../ts';
import {
  ActionSkillCategories,
  AnalyticsActionSkillTypes
} from '../../../ts/action';
import type { AnalyticsSlice } from '../../../ts/state';
import {
  getAnalyticsReports,
  getSingleActionHistogram,
  getGenerativeAiMetrics,
  getMessageLogs,
  getconvoSearchMessageLogs,
  getAllLogs,
  getSingleConversation,
  getActionCompletion,
  getTopicsReport,
  getSingleActionRequests,
  MAX_LOGS
} from './actions';
import { recognitionReducers } from './ducks/recognitionReducers';
import { singleRecognizedGroupReducers } from './ducks/singleRecognizedGroupReducers';

const CONVO_SEARCH_RESP_FILTERS = {
  ALL: 'all',
  SUCCESS: 'successful',
  IDK: 'idk'
};

// Initial State
const initialState: AnalyticsSlice = {
  filters: {
    environment: '',
    range: '',
    startDate: '',
    endDate: '',
    interval: '',
    search: '',
    containment: '',
    recognition: '',
    noIntents: false,
    sortOrder: 'desc',
    topics: [],
    entities: [],
    resolution_method: [],
    keywords: [],
    completeReasons: [],
    incompleteReasons: [],
    percentageReasons: [],
    convoSearchResponseType: CONVO_SEARCH_RESP_FILTERS.ALL
  },
  skillHasData: {
    loading: ApiStatus.IDLE,
    error: null,
    hasCoexistenceData: false,
    hasData: false
  },
  intentActionPairs: {
    loading: ApiStatus.IDLE,
    error: null,
    hasIntentActionPairs: false,
    intentActionPairs: []
  },
  messages: {
    loading: ApiStatus.IDLE,
    error: null,
    total: null,
    reports: []
  },
  conversations: {
    loading: ApiStatus.IDLE,
    error: null,
    total: null,
    reports: [['', 0]]
  },
  activeUsers: {
    loading: ApiStatus.IDLE,
    error: null,
    total: null,
    reports: [['', 0]]
  },
  userRequests: {
    loading: ApiStatus.IDLE,
    error: null,
    total: null,
    reports: [['', 0]]
  },
  mostPopular: {
    loading: ApiStatus.IDLE,
    error: null,
    topics: [],
    actions: []
  },
  leastPopular: {
    loading: ApiStatus.IDLE,
    error: null,
    topics: [],
    actions: []
  },
  frequentActions: {
    loading: ApiStatus.IDLE,
    error: null,
    mostFrequent: [],
    leastFrequent: []
  },
  intentContainment: {
    loading: ApiStatus.IDLE,
    error: null,
    topics: []
  },
  messageLogs: {
    requests: [],
    loading: ApiStatus.IDLE,
    error: null,
    loaded: false,
    total: 0,
    pagination: {
      currentPageSize: 100,
      currentPage: 1,
      page_after_key: {},
      lastResult: false,
      approxTotal: 0
    }
  },
  convoSearchMessageLogs: {
    responseLength: 0,
    responseConfidence: 0,
    retrievalConfidence: 0,
    extractiveness: 0,
    citation: 0,
    requests: [],
    loading: ApiStatus.IDLE,
    error: null,
    loaded: false,
    total: 0,
    pagination: {
      currentPageSize: 100,
      currentPage: 1,
      page_after_key: {},
      lastResult: false,
      approxTotal: 0
    }
  },
  allLogs: {
    coexistence: false, // true if messages are coming from dialog interaction
    requests: [],
    loading: ApiStatus.IDLE,
    error: null,
    loaded: false,
    total: 0,
    pagination: {
      currentPageSize: 100,
      currentPage: 1,
      page_after_key: {},
      lastResult: false,
      approxTotal: 0
    }
  },
  singleConversation: {
    convo: {},
    loading: ApiStatus.IDLE,
    error: null
  },
  coverage: {
    loading: ApiStatus.IDLE,
    error: null,
    covered: {
      total: null,
      reports: []
    },
    uncovered: {
      total: null,
      reports: []
    },
    percentage: {
      total: null,
      reports: []
    }
  },
  containment: {
    loading: ApiStatus.IDLE,
    error: null,
    contained: {
      total: null,
      reports: []
    },
    uncontained: {
      total: null,
      reports: []
    },
    percentage: {
      total: null,
      reports: []
    }
  },
  actionCompletion: {
    actions: [],
    loading: ApiStatus.IDLE,
    error: null
  },
  actionCompletionHistogram: {
    loading: ApiStatus.IDLE,
    error: null,
    complete: {
      total: 0,
      reports: [['', '', 0]]
    },
    incomplete: {
      total: 0,
      reports: [['', '', 0]]
    },
    percentage: {
      total: 0,
      reports: [['', '', 0]]
    }
  },
  recognition: {
    loading: ApiStatus.IDLE,
    error: null,
    recognized: {
      total: 0,
      reports: [['', '', 0]]
    },
    unrecognized: {
      total: 0,
      reports: [['', '', 0]]
    },
    percentage: {
      total: 0,
      reports: [['', '', 0]]
    }
  },
  recognitionClusters: {
    groups: [],
    loading: ApiStatus.IDLE,
    error: null,
    status: null,
    total: 0
  },
  generativeAiMetrics: {
    loading: ApiStatus.IDLE,
    error: null,
    request_routing: {
      unrecognized: 0,
      skill_based_action: 0,
      ai_guided_action: 0,
      custom_built_action: 0,
      conversational_search: 0,
      general_purpose_answering: 0
    },
    conversational_search: {
      extractiveness: 0,
      retrieval_confidence: 0,
      response_confidence: 0,
      citations_per_response: 0,
      response_length: 0
    }
  },
  singleActionHistogram: {
    loading: ApiStatus.IDLE,
    error: null,
    complete: {
      agentHandoff: {
        total: 0,
        reports: [['', '', 0]]
      },
      assistant: {
        total: 0,
        reports: [['', '', 0]]
      }
    },
    incomplete: {
      escalated: {
        total: 0,
        reports: [['', '', 0]]
      },
      newAction: {
        total: 0,
        reports: [['', '', 0]]
      },
      stuckStep: {
        total: 0,
        reports: [['', '', 0]]
      },
      triggerPhrase: {
        total: 0,
        reports: [['', '', 0]]
      },
      other: {
        total: 0,
        reports: [['', '', 0]]
      }
    },
    percentage: {
      total: 0,
      reports: [['', 0, '']],
      completeActions: [['', '', 0]],
      incompleteActions: [['', '', 0]]
    }
  },
  singleActionPercentage: {
    requests: [],
    loading: ApiStatus.IDLE,
    total: 0,
    pagination: {
      currentPageSize: 50,
      currentPage: 1,
      page_after_key: {},
      lastResult: false
    },
    error: null
  },
  singleActionIncomplete: {
    requests: [],
    loading: ApiStatus.IDLE,
    total: 0,
    pagination: {
      currentPageSize: 50,
      currentPage: 1,
      page_after_key: {},
      lastResult: false
    },
    error: null
  },
  singleActionComplete: {
    requests: [],
    loading: ApiStatus.IDLE,
    total: 0,
    pagination: {
      currentPageSize: 50,
      currentPage: 1,
      page_after_key: {},
      lastResult: false
    },
    error: null
  },
  singleUnrecognizedGroup: {
    action: '',
    total: 0,
    examples: [],
    groups: [],
    loading: ApiStatus.IDLE,
    error: null
  }
};

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (updates?: AnalyticsSlice): AnalyticsSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

const stateObjMapping = {
  totalMessages: 'messages',
  totalConversations: 'conversations',
  totalActiveUsers: 'activeUsers',
  totalUserRequests: 'userRequests',
  coverage: 'coverage',
  containment: 'containment',
  actionCompletionHistogram: 'actionCompletionHistogram',
  recognition: 'recognition',
  percentage: 'singleActionPercentage',
  incomplete: 'singleActionIncomplete',
  complete: 'singleActionComplete',
  mostFrequent: 'mostPopular',
  leastFrequent: 'leastPopular',
  actionCompletion: 'actionCompletion',
  frequentActions: 'frequentActions',
  singleActionHistogram: 'singleActionHistogram',
  skillHasData: 'skillHasData',
  intentActionPairs: 'intentActionPairs',
  unrecognizedCharts: 'recognition' //Chart on Recognition page
};

// Slice + Reducer
const slice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    clearAllFilters: (state: AnalyticsSlice) => {
      state.filters = initialState.filters;
    },
    clearRecognitionClusters: state => {
      state.recognitionClusters = initialState.recognitionClusters;
    },
    setEnvironment: (state: AnalyticsSlice, action: AnyAction) => {
      state.filters = {
        ...state.filters,
        environment: action.payload
      };
    },
    setFilterParams: (state: AnalyticsSlice, action: AnyAction) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
      state.recognitionClusters = initialState.recognitionClusters;
    },
    setPageConversation: (state: AnalyticsSlice, action: AnyAction) => {
      const { currentPage } = action.payload;
      state.messageLogs.pagination.currentPage = currentPage;
    },
    setPageAllLogs: (state: AnalyticsSlice, action: AnyAction) => {
      const { currentPage } = action.payload;
      state.allLogs.pagination.currentPage = currentPage;
    },
    // Setting current page index for convoSearchMessageLogs
    setPageAllConvoSearchLogs: (state: AnalyticsSlice, action: AnyAction) => {
      const { currentPage } = action.payload;
      state.convoSearchMessageLogs.pagination.currentPage = currentPage;
    },
    setPageSingleAction: (state: AnalyticsSlice, action: AnyAction) => {
      const { currentPage, type } = action.payload;
      const objType = stateObjMapping[type];
      state[objType].pagination.currentPage = currentPage;
    },
    setConvoSearchResponseType: (state: AnalyticsSlice, action: AnyAction) => {
      state.filters = {
        ...state.filters,
        convoSearchResponseType: action.payload
      };
    }
  },
  extraReducers: {
    ...recognitionReducers,
    ...singleRecognizedGroupReducers,
    [getAnalyticsReports.pending.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const objType = stateObjMapping[action.meta.arg];
      state[objType].loading = ApiStatus.LOADING;
      state[objType].error = null;
    },
    [getAnalyticsReports.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const { data, filters, type } = action.payload;
      const totalMapping = {
        totalMessages: 'messages_total',
        totalConversations: 'conversations_total',
        totalActiveUsers: 'active_users_total',
        totalUserRequests: 'user_requests_total'
      };

      let formattedData = [];
      if (type === 'intentActionPairs') {
        state[type].hasIntentActionPairs = data.hasIntentActionPairs;
        state[type].intentActionPairs = data.intentActionPairs;
        state[type].loading = ApiStatus.SUCCEEDED;
      } else if (type === 'skillHasData') {
        state[type].hasCoexistenceData = data.hasCoexistenceData;
        state[type].hasData = data.hasData;
        state[type].loading = ApiStatus.SUCCEEDED;
      } else if (type === 'coverage') {
        const coveredData = formatData(data.reports, filters, 'covered');
        const unCoveredData = formatData(data.reports, filters, 'uncovered');
        const coveredPercentage = formatData(
          data.reports,
          filters,
          'coveredPercentage'
        );

        state[type].loading = ApiStatus.SUCCEEDED;
        state[type].covered.total = data.totals.covered_total;
        state[type].covered.reports = coveredData;
        state[type].uncovered.total = data.totals.uncovered_total;
        state[type].uncovered.reports = unCoveredData;
        state[type].percentage.total = data.totals.covered_percent_total;
        state[type].percentage.reports = coveredPercentage;
      } else if (type === 'containment') {
        const containedData = formatData(data.reports, filters, 'contained');
        const unContainedData = formatData(
          data.reports,
          filters,
          'uncontained'
        );
        const containedPercentage = formatData(
          data.reports,
          filters,
          'containedPercentage'
        );

        state[type].loading = ApiStatus.SUCCEEDED;
        state[type].contained.total = data.totals.contained_total;
        state[type].contained.reports = containedData;
        state[type].uncontained.total = data.totals.uncontained_total;
        state[type].uncontained.reports = unContainedData;
        state[type].percentage.total = data.totals.contained_percent_total;
        state[type].percentage.reports = containedPercentage;
      } else if (type === 'actionCompletionHistogram') {
        const incompleteData = formatData(data.reports, filters, 'incomplete');
        const completeData = formatData(data.reports, filters, 'complete');
        const completionPercentage = formatData(
          data.reports,
          filters,
          'completionPercentage'
        );
        state[type].loading = ApiStatus.SUCCEEDED;
        state[type].complete.total = data.totals.complete;
        state[type].complete.reports = completeData;
        state[type].incomplete.total = data.totals.incomplete;
        state[type].incomplete.reports = incompleteData;
        state[type].percentage.total = data.totals.complete_percent;
        state[type].percentage.reports = completionPercentage;
      } else if (type === 'recognition') {
        const unrecognized = formatData(data.reports, filters, 'unrecognized');
        const recognized = formatData(data.reports, filters, 'recognized');
        const recognizedPercentage = formatData(
          data.reports,
          filters,
          'recognizedPercentage'
        );
        state[type].loading = ApiStatus.SUCCEEDED;
        state[type].recognized.total = data.totals.recognized_total;
        state[type].recognized.reports = recognized;
        state[type].unrecognized.total = data.totals.unrecognized_total;
        state[type].unrecognized.reports = unrecognized;
        state[type].percentage.total = data.totals.recognized_percent_total;
        state[type].percentage.reports = recognizedPercentage;
      } else {
        const mapValue = stateObjMapping[type];
        const totalValue = totalMapping[type];

        formattedData = formatData(data.reports, filters, type);
        state[mapValue].loading = ApiStatus.SUCCEEDED;
        state[mapValue].total = data.totals[totalValue];
        state[mapValue].reports = formattedData;
      }
    },
    [getAnalyticsReports.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const objType = stateObjMapping[action.meta.arg];
      state[objType].loading = ApiStatus.ERROR;
      state[objType].error = action.error;
    },
    [getGenerativeAiMetrics.pending.toString()]: (state: AnalyticsSlice) => {
      state.generativeAiMetrics.loading = ApiStatus.LOADING;
      state.generativeAiMetrics.error = null;
    },
    [getGenerativeAiMetrics.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      state.generativeAiMetrics.loading = ApiStatus.SUCCEEDED;
      state.generativeAiMetrics.request_routing =
        action.payload.reports.request_routing;
      state.generativeAiMetrics.conversational_search =
        action.payload.reports.conversational_search;
    },
    [getGenerativeAiMetrics.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      state.generativeAiMetrics = { ...initialState.generativeAiMetrics };
      state.generativeAiMetrics.loading = ApiStatus.ERROR;
      state.generativeAiMetrics.error = action.error;
    },
    [getSingleActionHistogram.pending.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const objType = stateObjMapping[action.meta.arg.type];
      state[objType].loading = ApiStatus.LOADING;
      state[objType].error = null;
    },
    [getSingleActionHistogram.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const { data, filters } = action.payload;
      const objType = stateObjMapping[action.meta.arg.type];
      const assistantData = formatData(data.reports, filters, 'assistant');
      const agentHandoffData = formatData(
        data.reports,
        filters,
        'agentHandoff'
      );
      const escalatedData = formatData(data.reports, filters, 'escalated');
      const newActionData = formatData(data.reports, filters, 'newAction');
      const stuckStepData = formatData(data.reports, filters, 'stuckStep');
      const triggerPhraseData = formatData(
        data.reports,
        filters,
        'triggerPhrase'
      );
      const otherData = formatData(data.reports, filters, 'other');
      const percentageData = formatData(
        data.reports,
        filters,
        'singleActionCompletion'
      );
      const completeData = formatData(data.reports, filters, 'complete');
      const incompleteData = formatData(data.reports, filters, 'incomplete');
      state[objType].complete.agentHandoff.total =
        data.totals.planned_agent_handoff;
      state[objType].complete.agentHandoff.reports = agentHandoffData;
      state[objType].complete.assistant.total = data.totals.assistant;
      state[objType].complete.assistant.reports = assistantData;
      state[objType].incomplete.escalated.total = data.totals.escalated;
      state[objType].incomplete.escalated.reports = escalatedData;
      state[objType].incomplete.newAction.total =
        data.totals.started_new_action;
      state[objType].incomplete.newAction.reports = newActionData;
      state[objType].incomplete.triggerPhrase.total =
        data.totals.trigger_phrase;
      state[objType].incomplete.triggerPhrase.reports = triggerPhraseData;
      state[objType].incomplete.stuckStep.total = data.totals.stuck_on_step;
      state[objType].incomplete.stuckStep.reports = stuckStepData;
      state[objType].incomplete.other.total = data.totals.other;
      state[objType].incomplete.other.reports = otherData;
      state[objType].percentage.reports = percentageData;
      state[objType].percentage.completeActions = completeData;
      state[objType].percentage.incompleteActions = incompleteData;
      state[objType].percentage.total = data.totals.complete_percent;
      state[objType].loading = ApiStatus.SUCCEEDED;
    },
    [getSingleActionHistogram.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const objType = stateObjMapping[action.meta.arg.type];
      state[objType].loading = ApiStatus.ERROR;
      state[objType].error = action.error;
    },
    [getMessageLogs.pending.toString()]: (state: AnalyticsSlice) => {
      state.messageLogs.loading = ApiStatus.LOADING;
      state.messageLogs.error = null;
      state.messageLogs.loaded = false;
    },
    [getconvoSearchMessageLogs.pending.toString()]: (state: AnalyticsSlice) => {
      state.convoSearchMessageLogs.loading = ApiStatus.LOADING;
      state.convoSearchMessageLogs.error = null;
      state.convoSearchMessageLogs.loaded = false;
    },
    [getAllLogs.pending.toString()]: (state: AnalyticsSlice) => {
      state.allLogs.loading = ApiStatus.LOADING;
      state.allLogs.error = null;
      state.allLogs.loaded = false;
    },
    [getMessageLogs.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const { actions, logs, requests, initialCall } = action.payload;
      if (initialCall) {
        state.messageLogs.pagination.currentPage = 1;
        state.messageLogs.pagination.lastResult = false;
      }
      const newMessages = formatLogsData(
        logs?.results,
        actions,
        requests?.length
      );

      const concatMessages = requests?.concat(newMessages);
      state.messageLogs.requests = concatMessages;
      state.messageLogs.total = concatMessages?.length;
      state.messageLogs.pagination.lastResult = logs?.results.length < MAX_LOGS;
      state.messageLogs.loading = ApiStatus.SUCCEEDED;
      state.messageLogs.pagination.page_after_key = logs?.page_after_key;
      state.messageLogs.pagination.approxTotal = logs?.total_requests;
      state.messageLogs.loaded = true;
    },
    [getconvoSearchMessageLogs.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const { logs, requests, initialCall } = action.payload;
      if (initialCall) {
        state.convoSearchMessageLogs.pagination.currentPage = 1;
        state.convoSearchMessageLogs.pagination.lastResult = false;
      }
      const newMessages = formatConvoSearchLogs(
        logs?.results,
        requests?.length
      );

      const concatMessages = requests?.concat(newMessages);
      state.convoSearchMessageLogs.requests = concatMessages;
      state.convoSearchMessageLogs.total = concatMessages?.length;
      state.convoSearchMessageLogs.pagination.lastResult =
        logs?.results.length < MAX_LOGS;
      state.convoSearchMessageLogs.loading = ApiStatus.SUCCEEDED;
      state.convoSearchMessageLogs.pagination.page_after_key =
        logs?.page_after_key;
      state.convoSearchMessageLogs.pagination.approxTotal =
        logs?.messages_total;
      state.convoSearchMessageLogs.loaded = true;
      state.convoSearchMessageLogs.responseLength = logs?.response_length;
      state.convoSearchMessageLogs.responseConfidence =
        logs?.response_confidence;
      state.convoSearchMessageLogs.retrievalConfidence =
        logs?.retrieval_confidence;
      state.convoSearchMessageLogs.extractiveness = logs?.extractiveness;
      state.convoSearchMessageLogs.citation = logs?.citations_per_response;
    },
    [getAllLogs.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const { actions, logs, requests, initialCall } = action.payload;
      if (initialCall) {
        state.allLogs.pagination.currentPage = 1;
        state.allLogs.pagination.lastResult = false;
      }
      const newMessages = formatAllLogs(
        logs?.results,
        actions,
        requests?.length
      );
      const concatMessages = requests?.concat(newMessages);
      state.allLogs.requests = concatMessages;
      state.allLogs.total = concatMessages?.length;
      state.allLogs.pagination.lastResult = logs?.results.length < MAX_LOGS;
      state.allLogs.loading = ApiStatus.SUCCEEDED;
      state.allLogs.pagination.page_after_key = logs?.page_after_key;
      state.allLogs.pagination.approxTotal = logs?.messages_total;
      state.allLogs.coexistence = logs.coexistence;
      state.allLogs.loaded = true;
    },
    [getAllLogs.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      state.allLogs.loading = ApiStatus.ERROR;
      state.allLogs.error = action.error;
      state.allLogs.loaded = false;
    },
    [getMessageLogs.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      state.messageLogs.loading = ApiStatus.ERROR;
      state.messageLogs.error = action.error;
      state.messageLogs.loaded = false;
    },
    [getconvoSearchMessageLogs.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      state.convoSearchMessageLogs.loading = ApiStatus.ERROR;
      state.convoSearchMessageLogs.error = action.error;
      state.convoSearchMessageLogs.loaded = false;
    },
    [getSingleConversation.pending.toString()]: (state: AnalyticsSlice) => {
      state.singleConversation.loading = ApiStatus.LOADING;
      state.singleConversation.error = null;
    },
    [getSingleConversation.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      state.singleConversation.loading = ApiStatus.SUCCEEDED;
      state.singleConversation.convo = action.payload.results;
    },
    [getSingleConversation.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      state.singleConversation.loading = ApiStatus.ERROR;
      state.singleConversation.error = action.error;
    },
    [getTopicsReport.pending.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      state[action.meta.arg.type].loading = ApiStatus.LOADING;
      state[action.meta.arg.type].error = null;
    },
    [getTopicsReport.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const { type, results, actions } = action.payload;
      const data = formatTopIntentsData(results, actions, 'coexist');
      state[type].loading = ApiStatus.SUCCEEDED;
      state[type].topics = data;
    },
    [getTopicsReport.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      state[action.meta.arg.type].loading = ApiStatus.ERROR;
      state[action.meta.arg.type].error = action.error;
    },
    [getActionCompletion.pending.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const objType = stateObjMapping[action.meta.arg.type];
      state[objType].loading = ApiStatus.LOADING;
      state[objType].error = null;
    },
    [getActionCompletion.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const { results, actions, isOverview } = action.payload;
      const objType = stateObjMapping[action.meta.arg.type];
      let formattedActions;
      if (objType === 'actionCompletion') {
        formattedActions = formatActionCompletionData(
          action.payload,
          isOverview
        );
        state[objType].actions = formattedActions;
      } else {
        const mostFrequent = formatTopIntentsData(
          results.most_frequent,
          actions
        );
        const leastFrequent = formatTopIntentsData(
          results.least_frequent,
          actions
        );
        state[objType].mostFrequent = mostFrequent;
        state[objType].leastFrequent = leastFrequent;
      }
      state[objType].loading = ApiStatus.SUCCEEDED;
    },
    [getActionCompletion.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const objType = stateObjMapping[action.meta.arg.type];
      state[objType].loading = ApiStatus.ERROR;
      state[objType].error = action.error;
    },
    [getSingleActionRequests.pending.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const objType = stateObjMapping[action.meta.arg.type];
      state[objType].loading = ApiStatus.LOADING;
      state[objType].error = null;
    },
    [getSingleActionRequests.fulfilled.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const { data, type, prevRequests, initialCall } = action.payload;
      const objType = stateObjMapping[type];

      if (initialCall) {
        state[objType].pagination.currentPage = 1;
        state[objType].pagination.lastResult = false;
      }

      const formattedRequests = formatSingleActionData(data.results);
      const concatRequests = prevRequests?.concat(formattedRequests);
      state[objType].loading = ApiStatus.SUCCEEDED;
      state[objType].requests = concatRequests;
      state[objType].total = data.total_requests;
      state[objType].pagination.page_after_key = data?.page_after_key;
      state[objType].pagination.lastResult = data.results?.length < 50;
    },
    [getSingleActionRequests.rejected.toString()]: (
      state: AnalyticsSlice,
      action: AnyAction
    ) => {
      const objType = stateObjMapping[action.meta.arg.type];
      state[objType].loading = ApiStatus.ERROR;
      state[objType].error = action.error;
      state[objType].requests = [];
      state[objType].pagination.currentPage = 1;
      state[objType].pagination.lastResult = true;
      state[objType].total = 0;
    }
  }
});

const getTypeMapping = (type: string): string => {
  const typeMapping = {
    totalMessages: 'Messages',
    totalConversations: 'Conversations',
    totalActiveUsers: 'Customers',
    totalUserRequests: 'Requests',
    covered: 'Covered',
    uncovered: 'Uncovered',
    contained: 'Contained',
    uncontained: 'Uncontained',
    coveredPercentage: 'coveredPercentage',
    containedPercentage: 'containedPercentage',
    complete: 'Complete',
    incomplete: 'Incomplete',
    recognized: 'Recognized',
    unrecognized: 'Unrecognized',
    recognizedPercentage: 'recognizedPercentage',
    completionPercentage: 'completionPercentage',
    assistant: 'Assistant',
    agentHandoff: 'agentHandoff',
    escalated: 'Escalated',
    newAction: 'New',
    stuckStep: 'Step',
    triggerPhrase: 'Trigger',
    other: 'Other',
    singleActionCompletion: 'singleActionCompletion'
  };

  return typeMapping[type];
};
//return the correct format of data reports
const formatData = (data, filters, type) => {
  const isHourInterval = filters.interval === 'Hour';
  const categoryType = getTypeMapping(type);
  const emptyData = addEmptyData(categoryType, filters, data, isHourInterval);
  let formattedData = addApiData(
    categoryType,
    filters,
    data,
    emptyData,
    isHourInterval
  );
  const dataToCurrent = addEmptyDataAfter(
    categoryType,
    filters,
    data,
    isHourInterval
  );

  formattedData = formattedData.concat(dataToCurrent);
  return formattedData;
};

//add api response data to the array
const addApiData = (type, filters, data, dataArray, isHourInterval) => {
  const { interval } = filters;
  const objMapping = {
    Messages: 'messages',
    Conversations: 'conversations',
    Customers: 'active_users',
    Requests: 'user_requests',
    Covered: 'covered',
    Uncovered: 'uncovered',
    coveredPercentage: 'covered_percent',
    Contained: 'contained',
    Uncontained: 'uncontained',
    containedPercentage: 'contained_percent',
    Complete: 'complete',
    Incomplete: 'incomplete',
    Recognized: 'recognized',
    Unrecognized: 'unrecognized',
    recognizedPercentage: 'recognized_percent',
    completionPercentage: 'complete_percent',
    Assistant: 'assistant',
    agentHandoff: 'planned_agent_handoff',
    Escalated: 'escalated',
    New: 'started_new_action',
    Step: 'stuck_on_step',
    Trigger: 'trigger_phrase',
    Other: 'other',
    singleActionCompletion: 'complete_percent'
  };
  const elementType = objMapping[type];
  const checkPercentage = isTypePercentage(type);
  const isEngagement =
    type === 'Customers' || type === 'Conversations' || type === 'Requests';
  _.forEach(data, element => {
    const timestamp = !isHourInterval
      ? interval !== 'Month'
        ? moment(element.timestamp).format('DD MMM')
        : moment.utc(element.timestamp).format('MMMM')
      : moment(element.timestamp).format('DD MMM [at] h:mm A');
    const value = element[elementType];

    if (!isEngagement) {
      //if percentage type, only 2 elements should be in the array
      dataArray?.push([
        timestamp,
        ...(!checkPercentage ? [type] : []),
        ...(!checkPercentage ? [value] : [value.toString() + '%'])
      ]);
    } else {
      dataArray.push([timestamp, value]);
    }
  });

  return dataArray;
};

//adds empty data up to to first date which has data
const addEmptyData = (type, filters, data, isHourInterval) => {
  const { startDate, interval, endDate } = filters;
  const intervalMapping = {
    Hour: 'day',
    Day: 'day',
    Week: 'isoWeek',
    Month: 'month'
  };

  const start = moment(startDate).startOf(intervalMapping[interval]);
  const end = moment(endDate).endOf(intervalMapping[interval]);
  const duration = moment.duration(end.diff(start));
  const expectedLength = {
    Hour: Math.ceil(duration.asHours()),
    Day: Math.ceil(duration.asDays()),
    Week: Math.ceil(duration.asWeeks()),
    Month: Math.round(duration.asMonths())
  };
  const prevData = [];
  const checkPercentage = isTypePercentage(type);
  const isEngagement =
    type === 'Customers' || type === 'Conversations' || type === 'Requests';

  if (data?.length === 0) {
    let newDate = moment(start).toDate();
    for (let i = 0; i < expectedLength[interval]; i++) {
      const timestamp = !isHourInterval
        ? interval !== 'Month'
          ? moment(newDate).format('DD MMM')
          : moment.utc(newDate).format('MMMM')
        : moment(newDate).format('DD MMM [at] h:mm A');
      if (!isEngagement) {
        prevData.push([
          timestamp,
          ...(!checkPercentage ? [type] : []),
          ...(!checkPercentage ? [0] : ['0%'])
        ]);
      } else {
        prevData.push([timestamp, 0]);
      }
      newDate = moment(newDate).add(1, interval).toDate();
    }
  } else if (data !== undefined) {
    let firstDataDate;
    if (isHourInterval) {
      firstDataDate = moment(data[0].timestamp).toDate();
    } else {
      firstDataDate = moment(data[0].timestamp).startOf(
        intervalMapping[interval]
      );
    }
    let newDate = null;
    while (
      start.toDate() < firstDataDate &&
      data?.length < expectedLength[interval]
    ) {
      newDate = moment(firstDataDate).subtract(1, interval).toDate();
      const timestamp = !isHourInterval
        ? interval !== 'Month'
          ? moment(newDate).format('DD MMM')
          : moment.utc(newDate).format('MMMM')
        : moment(newDate).format('DD MMM [at] h:mm A');
      if (!isEngagement) {
        prevData.push([
          timestamp,
          ...(!checkPercentage ? [type] : []),
          ...(!checkPercentage ? [0] : ['0%'])
        ]);
      } else {
        prevData.push([timestamp, 0]);
      }

      firstDataDate = newDate;
    }
  }
  return data?.length === 0 ? prevData : prevData.reverse();
};

//adds empty data upto from last date that has data to current date/time
const addEmptyDataAfter = (type, filters, data, isHourInterval) => {
  const { interval, endDate, range } = filters;
  const dataLength = data?.length;
  const lastTimestamp =
    dataLength !== 0 && dataLength !== undefined
      ? data[dataLength - 1].timestamp
      : '';
  let lastDate = lastTimestamp
    ? interval !== 'Month'
      ? moment(lastTimestamp)
      : moment.utc(lastTimestamp)
    : '';
  let now;
  if (range === 'custom') {
    const filterParamsEnd = moment(endDate);
    const currentDate = moment(new Date());
    if (filterParamsEnd.isSame(currentDate, 'day')) {
      now = moment().endOf('day');
    } else {
      now = filterParamsEnd.endOf('day');
    }
  } else {
    now = moment().endOf('day');
  }
  const diffDate = moment(now).diff(lastDate, interval);
  let newDate = null;
  const afterData = [];
  const checkPercentage = isTypePercentage(type);
  const isEngagement =
    type === 'Customers' || type === 'Conversations' || type === 'Requests';

  for (let i = 0; i < diffDate; i++) {
    newDate = moment(lastDate).add(1, interval);
    const timestamp = !isHourInterval
      ? interval !== 'Month'
        ? moment(newDate).format('DD MMM')
        : moment.utc(newDate).format('MMMM')
      : moment(newDate).format('DD MMM [at] h:mm A');
    if (!isEngagement) {
      afterData.push([
        timestamp,
        ...(!checkPercentage ? [type] : []),
        ...(!checkPercentage ? [0] : ['0%'])
      ]);
    } else {
      afterData.push([timestamp, 0]);
    }

    lastDate = newDate;
  }

  return afterData;
};

const isTypePercentage = type => {
  return (
    type === 'coveredPercentage' ||
    type === 'containedPercentage' ||
    type === 'recognizedPercentage' ||
    type === 'completionPercentage' ||
    type === 'singleActionCompletion'
  );
};

//format top intents report data
const formatTopIntentsData = (reports, actions, type = 'action') => {
  const formattedData = [];
  const isCoexist = type === 'coexist';
  if (!_.isEmpty(reports)) {
    formattedData.push({
      heading: true,
      firstHeading: isCoexist ? 'Topics' : 'Actions',
      secondHeading: 'Number started'
    });
  }

  reports?.forEach(report => {
    /*
      Backend does not send the action title,so the
      UI checks if the action is in the actions api call
    */
    let foundAction;
    if (isCoexist) {
      foundAction = _.filter(actions, item => item.id === report.intent);
    } else {
      foundAction = _.filter(actions, item => item.id === report.action_id);
    }

    formattedData.push({
      title:
        isCoexist && !report.intent.startsWith('action_')
          ? '#' + report.intent
          : foundAction.length > 0
          ? foundAction[0]?.title
          : report.action_title,
      actionID: isCoexist ? report.intent : report.action_id,
      value: isCoexist ? report.count : report.total_requests,
      deleted: isCoexist ? false : foundAction.length === 0,
      ...(report.skill_type && { skillType: report.skill_type })
    });
  });

  return formattedData;
};

// format the log data in the Conversations tab(View by recognition type) on the Analyze page
const formatLogsData = (logs, actions, requestsLength) => {
  const formattedLogs = [];
  let count = requestsLength;

  logs?.forEach(log => {
    _.forEach(log.root_messages, (message: any, index: number) => {
      const foundAction = actions.some(item => item.id === message.topic);
      formattedLogs.push({
        id: count.toString(),
        conversations: {
          timestamp: moment(log.earliest_timestamp).format(
            'DD MMM [at] h:mm A'
          ),
          conversationId: log.conversation_id,
          escalated: log.escalated,
          ...(log?.search_results && {
            searchApplied: true
          }),
          ...(message?.conversational_search_results && {
            convoSearchApplied: true
          }),
          start: index === 0,
          errors: message?.errors || []
        },
        topics: {
          topic: title(message, actions),
          type: whichType(message.skill_type),
          recognitionType: recognitionType(message?.recognition_type),
          ...(message?.search_results >= 0 && {
            results: message.search_results
          }),
          ...(message?.conversational_search_results >= 0 && {
            convoResults: message.conversational_search_results
          }),
          ...(message.skill_type === 'action_root' && {
            actionTitle: message.action_title
          }),
          unrecognized: message?.unrecognized || false,
          ...(message.skill_type === 'action_root' && {
            deleted: !foundAction
          }),
          //when action condition is not met, the value is false otherwise undefined. True by default
          conditionMet: message.condition_met ?? true,
          errors: message?.errors || []
        },
        requests: {
          type: message.skill_type,
          id: message.message_id,
          message: message.text,
          recognitionType: recognitionType(message.recognition_type)
        }
      });
      count++;
    });
  });

  return formattedLogs;
};

// format all logs in the Conversations tab(View by message log) on the Analyze page
const formatAllLogs = (logs, actions, requestsLength) => {
  const formattedLogs = [];
  let count = requestsLength;
  logs?.forEach(log => {
    formattedLogs.push({
      id: count.toString(),
      customerInput: {
        type: log?.skill_type,
        conversationId: log?.conversation_id,
        messageId: log?.message_id,
        message: log?.text,
        errors: log?.errors || []
      },
      entities: log?.entities || [],
      topics: {
        topic: title(log, actions),
        type: whichType(log?.skill_type),
        unrecognized: log?.unrecognized || false,
        recognitionType: recognitionType(log?.recognition_type),
        ...(log?.conversational_search_results >= 0 && {
          convoResults: log.conversational_search_results
        }),
        ...(log?.search_results >= 0 && {
          results: log.search_results
        }),
        errors: log?.errors || []
      },
      details: {
        timestamp: moment(log?.timestamp).format('DD MMM [at] h:mm A'),
        conversationId: log?.conversation_id,
        messageId: log?.message_id,
        ...(log?.search_results >= 0 && {
          searchApplied: true
        }),
        ...(log?.conversational_search_results >= 0 && {
          convoSearchApplied: true,
          searchApplied: true
        })
      }
    });
    count++;
  });
  return formattedLogs;
};

// format conversation log in the Conversational search tab on Analyze page
const formatConvoSearchLogs = (logs, requestsLength) => {
  const formattedLogs = [];
  let count = requestsLength;
  logs?.forEach(log => {
    formattedLogs.push({
      id: count.toString(),
      customerInput: {
        conversationId: log?.conversation_id,
        messageId: log?.message_id,
        message: log?.input,
        errors: log?.errors || [],
        ...(log?.inferred_query ? { inferredQuery: log?.inferred_query } : {})
      },
      response: {
        conversationId: log?.conversation_id,
        messageId: log?.message_id,
        response: log?.response,
        isIdkResponse: log?.is_idk_response,
        errors: log?.errors || [],
        // for IDK responses, we get back original idk response along with standard idk response
        ...(log?.is_idk_response
          ? { originalResponse: log?.original_response }
          : {})
      },
      responseConfidence: {
        conversationId: log?.conversation_id,
        messageId: log?.message_id,
        confidence: log?.response_confidence
      },
      retrievalConfidence: {
        conversationId: log?.conversation_id,
        messageId: log?.message_id,
        confidence: log?.retrieval_confidence
      },
      extractiveness: {
        conversationId: log?.conversation_id,
        messageId: log?.message_id,
        extractiveness: log?.extractiveness
      }
    });
    count++;
  });
  return formattedLogs;
};

//format table data in single action page
const formatSingleActionData = (requests = []) =>
  requests.map(result => ({
    id: result.message_id,
    requests: {
      message: result.request,
      conversationId: result.conversation_id,
      messageId: result.message_id,
      timestamp: moment(result.timestamp).format('DD MMMM h:mm A')
    },
    requestCompletion: _.capitalize(result.status),
    reason: result.reason
  }));

//return topic title
const title = (message, actions) => {
  const messageType = whichType(message.skill_type);
  if (messageType === 'intent') {
    if (message?.intents_irrelevant) {
      //If irrelevant use that as the topic name
      return '#Irrelevant';
    }
    return '#' + message.topic;
  } else if (
    messageType === 'coexist_action_fallback' ||
    messageType === 'action_fallback'
  ) {
    return 'Fallback';
  } else if (messageType === 'action_run_always') {
    return 'Trigger phrases';
  } else if (messageType === 'action_welcome') {
    return 'Greet customer';
  } else if (messageType === 'action' && message.topic === 'Irrelevant') {
    return message.topic;
  } else if (
    messageType === 'action' ||
    messageType === 'coexist_action_root' ||
    messageType === 'coexist_dialog_child_to_action_root' ||
    messageType === 'coexist_dialog_child_to_action_child'
  ) {
    const foundAction = _.filter(actions, item => item.id === message.topic);
    if (!_.isEmpty(foundAction)) {
      return foundAction[0].title;
    }
    return message.action_title;
  } else if (
    messageType === 'coexist_dialog_root_to_action_root' ||
    messageType === 'coexist_dialog_root_to_action_child'
  ) {
    const foundAction = _.filter(actions, item => item.id === message.topic);
    let topic = '#';

    if (message?.intents_irrelevant) {
      //If irrelevant use that as the topic name
      topic += 'Irrelevant';
    } else {
      topic += message.intent;
    }

    // If there is no action information just return the topic
    if (!message.action_title && !message.topic) {
      return topic;
    }

    topic += ' -@- ';

    if (!_.isEmpty(foundAction)) {
      return topic + foundAction[0].title;
    }

    return topic + message.action_title;
  } else if (messageType === 'action_child') {
    return '';
  }
  return messageType;
};

//return topic type
const whichType = type => {
  if (type === 'action_root' || type === 'coexist_action_root') {
    return 'action';
  } else if (type === 'dialog_root') {
    return 'intent';
  } else {
    return type;
  }
};

// format topic recognition_type
const recognitionType = type => {
  if (type === AnalyticsActionSkillTypes.AI_GUIDED) {
    return ActionSkillCategories.AI_GUIDED;
  } else if (type === AnalyticsActionSkillTypes.SKILL) {
    return ActionSkillCategories.CONVERSATIONAL_SKILL;
  } else if (type === AnalyticsActionSkillTypes.CUSTOM) {
    return ActionSkillCategories.STANDARD;
  } else if (type === AnalyticsActionSkillTypes.SYSTEM_ACTIONS) {
    return ActionSkillCategories.SYSTEM_ACTIONS;
  } else if (type === AnalyticsActionSkillTypes.INTENTS) {
    return ActionSkillCategories.INTENTS;
  } else if (type === AnalyticsActionSkillTypes.UNRECOGNIZED) {
    return ActionSkillCategories.UNRECOGNIZED;
  } else if (type === AnalyticsActionSkillTypes.ALL_RECOGNIZED) {
    return ActionSkillCategories.ALL_RECOGNIZED;
  } else if (type === AnalyticsActionSkillTypes.GENERAL_PURPOSE_ANSWERING) {
    return ActionSkillCategories.GENERAL_PURPOSE_ANSWERING;
  } else if (type === AnalyticsActionSkillTypes.CONVO_SEARCH) {
    return ActionSkillCategories.CONVO_SEARCH;
  } else if (type === AnalyticsActionSkillTypes.SEARCH) {
    return ActionSkillCategories.SEARCH;
  } else if (type === AnalyticsActionSkillTypes.SEARCH_ZERO_RESULTS_ONLY) {
    return ActionSkillCategories.SEARCH_ZERO_RESULTS_ONLY;
  } else {
    return type;
  }
};

//format action completion table data
const formatActionCompletionData = (payload, isOverview) => {
  const formattedData = [];
  const actions = payload.actions;
  payload?.results.forEach(result => {
    if (isOverview && result.complete_percent === 100) {
      return;
    }
    /*
      Get the updated action title
    */
    const foundAction = _.filter(actions, item => item.id === result.action_id);
    formattedData.push({
      id: result.action_id,
      actions: {
        title:
          foundAction.length > 0 ? foundAction[0]?.title : result.action_title,
        id: result.action_id,
        deleted: foundAction.length === 0
      },
      totalTraffic: result.total_requests,
      totalIncomplete: result.incomplete_requests,
      completion: result.complete_percent
    });
  });

  return formattedData;
};

//export functions that need to be tested. ONLY imported in the test file
export const testableFuncs = {
  formatData,
  addApiData,
  addEmptyData,
  addEmptyDataAfter
};

export default slice.reducer;
